import Loader from 'components/Icons/LoadingSpinner'
import { getDevice, useWalletConnect, WalletAdapterData, WalletButtonBaseProps } from 'connection/wallet-connect'
import { useCallback, useMemo, useState } from 'react'
import { ButtonProps } from 'rebass'
import styled from 'styled-components/macro'
import { flexColumnNoWrap, flexRowNoWrap } from 'theme/styles'
import { UserRejectedRequestError } from 'viem'
import { useAccount, useConnect } from 'wagmi'

import { openWindow } from '../../connection'

const OptionCardLeft = styled.div`
  ${flexColumnNoWrap};
  flex-direction: row;
  align-items: center;
`

const OptionCardClickable = styled.button<{ isActive?: boolean; clickable?: boolean }>`
  background-color: ${({ theme }) => theme.backgroundModule};
  width: 100% !important;
  border-color: transparent;

  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;

  margin-top: 0;
  transition: ${({ theme }) => theme.transition.duration.fast};
  opacity: ${({ disabled }) => (disabled ? '0.5' : '1')};
  &:hover {
    cursor: ${({ clickable }) => clickable && 'pointer'};
    background-color: ${({ theme, clickable }) => clickable && theme.borderPrimaryHover};
  }
`

const HeaderText = styled.div`
  ${flexRowNoWrap};
  align-items: center;
  justify-content: center;
  color: ${(props) => (props.color === 'blue' ? ({ theme }) => theme.accentAction : ({ theme }) => theme.textPrimary)};
  font-size: 16px;
  font-weight: 500;
  padding: 0 8px;
`

const IconWrapper = styled.div`
  ${flexColumnNoWrap};
  align-items: center;
  justify-content: center;
  flex: 0 0 40px;
  & > svg {
    width: 100%;
    height: 100%;
    border-radius: 50%;
  }
  ${({ theme }) => theme.deprecated_mediaWidth.deprecated_upToMedium`
    align-items: flex-end;
  `};
`

export type WalletButtonProps = Omit<WalletButtonBaseProps, 'children'> &
  WalletAdapterData & {
    ButtonProps?: ButtonProps
  }

export default function Option({
  ButtonProps,
  Icon,
  walletName,
  deeplink,
  getConnector,
  onConnect,
  detector,
}: WalletButtonProps) {
  const [isLoading, setIsLoading] = useState(false)
  const { isConnected } = useAccount()
  const { disconnectAsync } = useWalletConnect()
  // const { switchChainAsync } = useSwitchChain();
  const { connectAsync } = useConnect()

  const { isMobile, isTablet } = useMemo(getDevice, [])

  const handleClick = useCallback(async () => {
    setIsLoading(true)
    try {
      const isDetected = detector?.() ?? true

      if ((isMobile || isTablet) && deeplink && !isDetected) {
        openWindow(deeplink)
        return
      }

      if (!isDetected) return

      if (isConnected) {
        await disconnectAsync()
      }

      const connector = getConnector()

      /** @explanation some wallets require addEthereumChain to be called before connecting */
      // await switchChainAsync({ chainId });
      await connectAsync({ connector })
      await onConnect?.()
    } catch (e: unknown) {
      if (e instanceof UserRejectedRequestError) {
        console.warn('User rejected wallet connect request.', e)
      } else {
        throw e
      }
    } finally {
      setIsLoading(false)
    }
  }, [detector, isMobile, isTablet, deeplink, isConnected, getConnector, connectAsync, onConnect, disconnectAsync])

  return (
    <OptionCardClickable
      onClick={handleClick}
      clickable
      disabled={isLoading}
      data-testid="wallet-modal-option"
      {...ButtonProps}
    >
      <OptionCardLeft>
        <IconWrapper>
          <Icon />
        </IconWrapper>
        <HeaderText>{walletName}</HeaderText>
      </OptionCardLeft>
      {isLoading && <Loader />}
    </OptionCardClickable>
  )
}
